import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import type { RootState } from '../store/store';
import { updateStudySession } from '../services/studySession';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';
import {
  flashcardRemoved,
  flashcardsLoaded,
  flashcardSuspended,
  flashcardUpdated,
} from '../slices/flashcards/flashcardsSlice';

const completedFlashcardCount = (state: RootState): number =>
  (state.flashcards?.todaySessionCompletedBaseCount ?? 0) +
  (state.flashcards?.completed?.length ?? 0);

/**
 * Persists the completed flashcard count of today's session to the backend
 * (PATCH through `updateStudySession`) whenever one of the flashcard actions
 * below changes it, while the deck syncs to today's session.
 */
export const flashcardCountSyncListener = createListenerMiddleware({
  onError: (error) => {
    console.error('Error in the flashcard count sync listener:', error);
  },
});

flashcardCountSyncListener.startListening({
  matcher: isAnyOf(
    flashcardsLoaded,
    flashcardUpdated,
    flashcardSuspended,
    flashcardRemoved
  ),
  effect: (_action, listenerApi) => {
    const newState = listenerApi.getState() as RootState;

    if (newState.flashcards?.syncToTodaySession !== true) {
      return;
    }

    const prevCompletedCount = completedFlashcardCount(
      listenerApi.getOriginalState() as RootState
    );

    const newCompletedCount = completedFlashcardCount(newState);
    const todaySession = selectTodaySession(newState);

    // Only update if the count actually changed and we have a valid session
    if (
      newCompletedCount !== prevCompletedCount &&
      todaySession.id &&
      todaySession.completedFlashcardCount !== newCompletedCount
    ) {
      updateStudySession({
        id: todaySession.id,
        completedFlashcardCount: newCompletedCount,
      }).catch((error) => {
        console.error('Failed to sync completed flashcard count:', error);
      });
    }
  },
});
